import { v4 as uuid } from "uuid";

import { CLIENT_ID, HUDL_CLIENT_ID_DRAW, HUDL_ID_URL, LOGIN_API, REDIRECT_URI } from "../redux/constants";
import { AuthSlice, useDeleteUserCookieMutation, useLogoutMutation } from "../redux/slices";
import CacheUtils from "../utils/cache-utils";
import { paramify } from "../utils/url-utils";

export const useLogout = (token: AuthSlice["token"]) => {
  const [deleteUserCookie] = useDeleteUserCookieMutation();
  const [userLogout] = useLogoutMutation();

  const hasValidLoginCode = (loginCode: string) => {
    try {
      const decodedLoginCode = parseInt(atob(loginCode), 10) || 0;
      if (!loginCode || Date.now() - decodedLoginCode > 3600000) {
        return false;
      }
      return true;
    } catch (e) {
      return false;
    }
  };

  const logout = async () => {
    if (token) {
      localStorage.removeItem("createPlayDefaults");
      CacheUtils.clearSynergyCache();
      CacheUtils.clearVidSwapCache();
      CacheUtils.clearUserRequestedHref();
      CacheUtils.saveUserRequestedHref(window.location.hash.slice(1));
      CacheUtils.clearToken();

      await deleteUserCookie();
      userLogout({ token });
    }
    // Sleep a second to give the logout request time to process.
    await new Promise((r) => setTimeout(r, 1000));

    const urlParams = new URLSearchParams(window.location.search);
    const loginMode = urlParams.get("login");

    if (loginMode === "classic") {
      // Get loginCode from query params.  It's a base64 encoded unix timestamp in ms that's good for 1hour.  Show an error if it's not there,  or if it's older than 1 hour.
      const loginCode = urlParams.get("loginCode") || "";
      if (loginCode) {
        if (!hasValidLoginCode(loginCode)) {
          urlParams.delete("loginCode");
          window.history.replaceState({}, window.document.title, `${window.location.pathname}?${urlParams.toString()}`);
        } else {
          window.location.href = `${LOGIN_API}/authorize?response_type=token&client_id=${CLIENT_ID}&redirect_uri=${REDIRECT_URI}`;
          return;
        }
      }
    }

    const fmIdParams = paramify({
      redirect_url: REDIRECT_URI,
      client_id_fms: CLIENT_ID,
    });

    const fmIdUrl = new URL(LOGIN_API);
    fmIdUrl.pathname = "callback/hudl-auth";
    fmIdUrl.search = fmIdParams;

    const hudlIdParams = paramify({
      client_id: HUDL_CLIENT_ID_DRAW,
      redirect_uri: fmIdUrl.toString(),
      response_type: "code id_token",
      scope: "openid offline_access profile email",
      response_mode: "form_post",
      nonce: uuid(),
    });

    const hudlIdUrl = new URL(HUDL_ID_URL);
    hudlIdUrl.pathname = "authorize";
    hudlIdUrl.search = hudlIdParams;
    window.location.href = hudlIdUrl.toString();
  };

  return { logout };
};
